import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, Check, Plus, Trash2, Save, ArrowUp, ArrowDown } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/drinks")({
  head: () => ({ meta: [{ title: "İçecekler — JUËN Admin" }] }),
  component: AdminDrinksPage,
});

interface EventRow {
  id: string;
  name: string;
  starts_at: string;
  status: string;
}
interface DrinkRow {
  id?: string;
  name: string;
  description: string;
  price_kurus: number;
  sort_order: number;
  _new?: boolean;
  _delete?: boolean;
}
interface ProfileRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
}

function AdminDrinksPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState<EventRow[]>([]);
  const [drinks, setDrinks] = useState<DrinkRow[]>([]);
  const [drinksLoading, setDrinksLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Grant section
  const [grantEventId, setGrantEventId] = useState("");
  const [grantDrinkId, setGrantDrinkId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [results, setResults] = useState<ProfileRow[]>([]);
  const [selected, setSelected] = useState<ProfileRow | null>(null);
  const [searching, setSearching] = useState(false);
  const [granting, setGranting] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  const loadDrinks = async () => {
    setDrinksLoading(true);
    const { data, error } = await supabase
      .from("drinks")
      .select("*")
      .is("event_id", null)
      .eq("is_active", true)
      .order("sort_order");
    if (error) toast.error(error.message);
    setDrinks(
      (data ?? []).map((d) => ({
        id: d.id,
        name: d.name,
        description: d.description ?? "",
        price_kurus: d.price_kurus,
        sort_order: d.sort_order,
      })),
    );
    setDrinksLoading(false);
  };

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const { data } = await supabase
        .from("events")
        .select("id,name,starts_at,status")
        .order("starts_at", { ascending: false })
        .limit(100);
      const rows = (data as EventRow[]) ?? [];
      setEvents(rows);
      void rows;
    })();
    void loadDrinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const updateDrink = (idx: number, patch: Partial<DrinkRow>) =>
    setDrinks((arr) => arr.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  const addDrink = () =>
    setDrinks((arr) => [
      ...arr,
      { name: "", description: "", price_kurus: 0, sort_order: arr.length, _new: true },
    ]);
  const removeDrink = (idx: number) =>
    setDrinks((arr) => {
      const d = arr[idx];
      if (d._new) return arr.filter((_, i) => i !== idx);
      return arr.map((x, i) => (i === idx ? { ...x, _delete: true } : x));
    });
  const moveDrink = (idx: number, dir: -1 | 1) =>
    setDrinks((arr) => {
      // Build visible list (non-deleted) with their original indices
      const visible: { d: DrinkRow; i: number }[] = [];
      arr.forEach((d, i) => {
        if (!d._delete) visible.push({ d, i });
      });
      const pos = visible.findIndex((v) => v.i === idx);
      const swap = pos + dir;
      if (pos < 0 || swap < 0 || swap >= visible.length) return arr;
      const a = visible[pos].i;
      const b = visible[swap].i;
      const next = arr.slice();
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });

  const saveDrinks = async () => {
    setSaving(true);
    const toDelete = drinks.filter((d) => d._delete && d.id).map((d) => d.id!);

    // Assign sort_order based on current list order (visible only)
    let order = 0;
    const orderMap = new Map<number, number>();
    drinks.forEach((d, i) => {
      if (!d._delete) {
        orderMap.set(i, order);
        order += 1;
      }
    });

    const toInsert = drinks
      .map((d, i) => ({ d, i }))
      .filter(({ d }) => d._new && !d._delete && d.name.trim())
      .map(({ d, i }) => ({
        event_id: null,
        name: d.name.trim(),
        description: d.description || null,
        price_kurus: Math.round(d.price_kurus),
        sort_order: orderMap.get(i) ?? 0,
        abv_percent: 0,
        volume_ml: 0,
        shopier_payment_url: null,
      }));
    const toUpdate = drinks
      .map((d, i) => ({ d, i }))
      .filter(({ d }) => !d._new && !d._delete && d.id);

    if (toDelete.length) {
      const { error } = await supabase.from("drinks").delete().in("id", toDelete);
      if (error) {
        const { error: softErr } = await supabase
          .from("drinks")
          .update({ is_active: false })
          .in("id", toDelete);
        if (softErr) toast.error("İçecek silme hatası: " + softErr.message);
      }
    }
    if (toInsert.length) {
      const { error } = await supabase.from("drinks").insert(toInsert);
      if (error) toast.error("İçecek ekleme hatası: " + error.message);
    }
    for (const { d, i } of toUpdate) {
      const { error } = await supabase
        .from("drinks")
        .update({
          name: d.name.trim(),
          description: d.description || null,
          price_kurus: Math.round(d.price_kurus),
          sort_order: orderMap.get(i) ?? d.sort_order,
        })
        .eq("id", d.id!);
      if (error) toast.error("İçecek güncelleme hatası: " + error.message);
    }
    setSaving(false);
    toast.success("İçecekler kaydedildi");
    void loadDrinks();
  };

  // Member search
  useEffect(() => {
    const q = memberQuery.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      const like = `%${q}%`;
      const { data } = await supabase
        .from("profiles")
        .select("id,first_name,last_name,email,phone")
        .or(`email.ilike.${like},first_name.ilike.${like},last_name.ilike.${like},phone.ilike.${like}`)
        .limit(10);
      setResults((data as ProfileRow[]) ?? []);
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [memberQuery]);

  const grantableDrinks = useMemo(
    () => drinks.filter((d) => !d._new && !d._delete && d.id),
    [drinks],
  );

  const canGrant = useMemo(
    () => !!selected && !!grantDrinkId && quantity >= 1 && quantity <= 50,
    [selected, grantDrinkId, quantity],
  );

  const submitGrant = async () => {
    if (!canGrant || !selected) return;
    const drink = grantableDrinks.find((d) => d.id === grantDrinkId);
    if (!drink) return;
    const event = grantEventId ? events.find((e) => e.id === grantEventId) : null;
    const target = event ? `"${event.name}" etkinliği için ` : "(etkinlik bağımsız) ";
    if (
      !confirm(
        `${selected.first_name} ${selected.last_name} için ${target}${quantity} adet "${drink.name}" oluşturulacak. Onaylıyor musun?`,
      )
    )
      return;
    setGranting(true);
    const { error } = await supabase.rpc("admin_grant_drinks", {
      _user_id: selected.id,
      _event_id: grantEventId || (null as unknown as string),
      _drink_id: grantDrinkId,
      _quantity: quantity,
      _notes: notes.trim() || undefined,
    });
    setGranting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`${quantity} içecek kuponu oluşturuldu.`);
    setSelected(null);
    setMemberQuery("");
    setNotes("");
    setQuantity(1);
  };

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">İçecekler</h1>
          <p className="mt-2 text-muted-foreground">
            İçecekleri bir kez tanımla — tüm etkinliklerde kullanılabilir. Seçtiğin üyeye doğrudan içecek kuponu ver.
          </p>
        </div>

        <AdminTabs />

        <div className="space-y-6">
          {/* Global drink management */}
          <div className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">İçecek listesi (genel)</h2>
                <p className="text-sm text-muted-foreground">
                  Burada tanımladığın içecekler tüm etkinliklerde görünür.
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addDrink}>
                <Plus className="mr-1.5 h-4 w-4" /> İçecek ekle
              </Button>
            </div>

            {drinksLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Yükleniyor...</div>
            ) : (
              <div className="space-y-3">
                {drinks.filter((d) => !d._delete).length === 0 && (
                  <div className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
                    Henüz içecek yok.
                  </div>
                )}
                {drinks.map((d, idx) =>
                  d._delete ? null : (
                    <div key={d.id ?? `new-${idx}`} className="rounded-xl border border-border p-4">
                      <div className="grid gap-3 md:grid-cols-[1fr_140px_auto]">
                        <div>
                          <Label className="text-xs">Ad</Label>
                          <Input
                            value={d.name}
                            onChange={(e) => updateDrink(idx, { name: e.target.value })}
                            placeholder="Margarita"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Fiyat (TL)</Label>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={(d.price_kurus / 100).toString()}
                            onChange={(e) =>
                              updateDrink(idx, {
                                price_kurus: Math.round(parseFloat(e.target.value || "0") * 100),
                              })
                            }
                          />
                        </div>
                      <div className="flex items-end gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => moveDrink(idx, -1)}
                            title="Yukarı taşı"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => moveDrink(idx, 1)}
                            title="Aşağı taşı"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeDrink(idx)}
                            className="border-destructive/40 text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs">Açıklama</Label>
                        <Textarea
                          rows={2}
                          value={d.description}
                          onChange={(e) => updateDrink(idx, { description: e.target.value })}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}

            <Button onClick={saveDrinks} disabled={saving} className="w-full">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Değişiklikleri kaydet
            </Button>
          </div>

          {/* Grant section */}
          <div className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div>
              <h2 className="text-lg font-semibold">Üyeye içecek ver</h2>
              <p className="text-sm text-muted-foreground">
                Seçtiğin üyeye, seçtiğin etkinlik için ücretsiz içecek kuponu oluştur.
              </p>
            </div>

            <div>
              <Label>Etkinlik (opsiyonel)</Label>
              <select
                value={grantEventId}
                onChange={(e) => setGrantEventId(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Etkinlik bağımsız (genel kupon)</option>
                {events.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name} — {new Date(e.starts_at).toLocaleDateString("tr-TR")} ({e.status})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Boş bırakırsan kupon herhangi bir etkinlikte kullanılabilir.
              </p>
            </div>

            <div>
              <Label>İçecek</Label>
              <select
                value={grantDrinkId}
                onChange={(e) => setGrantDrinkId(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Seç...</option>
                {grantableDrinks.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({(d.price_kurus / 100).toLocaleString("tr-TR")} ₺)
                  </option>
                ))}
              </select>
              {grantableDrinks.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Önce içecek ekleyip kaydet, sonra üyeye verebilirsin.
                </p>
              )}
            </div>

            <div>
              <Label>Adet</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, Math.min(50, Number(e.target.value) || 1)))
                }
                className="mt-1.5 w-32"
              />
            </div>

            <div>
              <Label>Üye ara (e-posta, ad, telefon)</Label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={memberQuery}
                  onChange={(e) => {
                    setMemberQuery(e.target.value);
                    setSelected(null);
                  }}
                  placeholder="alican@..."
                  className="pl-9"
                />
              </div>
              {selected ? (
                <div className="mt-2 flex items-center justify-between rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
                  <div>
                    <div className="font-medium">
                      {selected.first_name} {selected.last_name}
                    </div>
                    <div className="text-muted-foreground">{selected.email}</div>
                  </div>
                  <Check className="h-5 w-5 text-primary" />
                </div>
              ) : memberQuery.trim().length >= 2 ? (
                <div className="mt-2 max-h-60 overflow-auto rounded-lg border border-border">
                  {searching ? (
                    <div className="p-3 text-sm text-muted-foreground">Aranıyor...</div>
                  ) : results.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">Sonuç yok.</div>
                  ) : (
                    results.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setSelected(r)}
                        className="flex w-full items-center justify-between border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted"
                      >
                        <span>
                          <span className="font-medium">
                            {r.first_name} {r.last_name}
                          </span>
                          <span className="ml-2 text-muted-foreground">{r.email}</span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>

            <div>
              <Label>Not (opsiyonel)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ör. İkram, telafi..."
                rows={2}
                className="mt-1.5"
              />
            </div>

            <Button onClick={submitGrant} disabled={!canGrant || granting} className="w-full">
              {granting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Üyeye gönder
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
